import { useEffect } from 'react';

import { isEqual } from 'lodash';
import PropTypes from 'prop-types';

import { Stack, Typography } from '@mui/material';

import { useTransl } from 'shared/lib/i18n';

import PersonFormComponent from '../Person/PersonFormComponent';

import { formatSelect, required } from '../Forms/utils';

import { CHECKBOX, REACT_SELECT_INPUT } from '../Forms/fieldTypes';

const AdminUserFormComponent = (props) => {
  const { t } = useTransl();

  const {
    isMasterPM,
    isService,
    pmByPmc,
    readOnlyApiAccess,
    pmcs,
    buildings,
    hasSelectedPmcs,
    onSelectExisting,
    onLoad,
    onSubmit,
    formValues,
    formInitialValues,
    selectExisting,
    initialValues,
    selectedBuildings,
    onFieldChange,
  } = props;

  const relevantBuildings = selectedBuildings?.filter((buildingId) =>
    buildings?.find((building) => building?.id === buildingId),
  );

  useEffect(() => {
    if (!hasSelectedPmcs && selectedBuildings?.length) {
      onFieldChange('buildings', []);
    }
  }, [hasSelectedPmcs]);

  useEffect(() => {
    if (!isEqual(relevantBuildings, selectedBuildings)) {
      onFieldChange('buildings', relevantBuildings);
    }
  }, [relevantBuildings]);

  const showPmcs = isMasterPM || pmByPmc;

  const pmByPmcLabel = (
    <Stack>
      <Typography flex={1}>{t('user.pmByPmc')}</Typography>
      {!!pmByPmc && (
        <Typography variant="body2">{t('user.pmByPmcNote')}</Typography>
      )}
    </Stack>
  );

  const additionalFormData = [
    {
      keyName: 'isService',
      label: t('user.ccAgent'),
      fieldType: CHECKBOX,
      validate: null,
      isHidden: isMasterPM || readOnlyApiAccess || pmByPmc,
    },
    {
      keyName: 'pmByPmc',
      label: pmByPmcLabel,
      fieldType: CHECKBOX,
      validate: null,
      isHidden: isMasterPM || readOnlyApiAccess || isService,
    },
    {
      keyName: 'readOnlyApiAccess',
      label: t('user.adminWithReadOnlyAccess'),
      fieldType: CHECKBOX,
      validate: null,
      isHidden: isMasterPM || isService || pmByPmc,
    },
    {
      keyName: 'pmcs',
      label: t('user.pmcs'),
      fieldType: REACT_SELECT_INPUT,
      multi: true,
      options: formatSelect(pmcs),
      validate: showPmcs ? [required] : [],
      isHidden: !showPmcs,
    },
    {
      keyName: 'buildings',
      label: t('title.building_s'),
      fieldType: REACT_SELECT_INPUT,
      options: formatSelect(buildings),
      multi: true,
      validate: [],
      isHidden:
        isService || isMasterPM || !hasSelectedPmcs || readOnlyApiAccess,
    },
  ];
  return (
    <PersonFormComponent
      title={t('actions.addAdmin')}
      formValues={formValues}
      formInitialValues={formInitialValues}
      additionalFormData={additionalFormData}
      onSelectExisting={onSelectExisting}
      onLoad={onLoad}
      onSubmit={onSubmit}
      selectExisting={selectExisting}
      initialValues={initialValues}
    />
  );
};

AdminUserFormComponent.defaultProps = {
  isMasterPM: true,
  isService: true,
  hasSelectedPmcs: false,
  selectExisting: {},
};

AdminUserFormComponent.propTypes = {
  pmcs: PropTypes.instanceOf(Array).isRequired,
  buildings: PropTypes.instanceOf(Array).isRequired,
  selectedBuildings: PropTypes.instanceOf(Array),
  onLoad: PropTypes.func.isRequired,
  onSelectExisting: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formValues: PropTypes.instanceOf(Object).isRequired,
  formInitialValues: PropTypes.instanceOf(Object).isRequired,
  initialValues: PropTypes.instanceOf(Object).isRequired,
  isMasterPM: PropTypes.bool,
  isService: PropTypes.bool,
  pmByPmc: PropTypes.bool,
  readOnlyApiAccess: PropTypes.bool,
  hasSelectedPmcs: PropTypes.bool,
  selectExisting: PropTypes.instanceOf(Object),
};

export default AdminUserFormComponent;
